/**
 * Returns SERVICES merged with localized text (title, shortTitle, tagline, description, features)
 * from the active language. Non-translatable fields (slug, icon, color, image, seoKeywords...) come
 * from the static SERVICES array below.
 */
export function getLocalizedServices(t) {
  return SERVICES.map((s) => {
    const loc = t(`services.items.${s.slug}`);
    if (!loc || typeof loc !== 'object') return s;
    return {
      ...s,
      title: loc.title || s.title,
      shortTitle: loc.shortTitle || s.shortTitle,
      tagline: loc.tagline || s.tagline,
      description: loc.description || s.description,
      features: Array.isArray(loc.features) ? loc.features : s.features,
    };
  });
}

export const SERVICES = [
  {
    slug: 'developpement-web-mobile',
    title: 'Développement Web & Mobile',
    shortTitle: 'Web & Mobile',
    icon: 'fa-solid fa-laptop-code',
    color: '#38c6f1',
    tagline: 'Des solutions numériques sur mesure qui propulsent votre croissance',
    description: 'Nexia Digital conçoit et développe des sites web vitrine, e-commerce, et applications mobiles iOS/Android performants, optimisés SEO et adaptés à tous les appareils. Nous utilisons les technologies les plus récentes pour vous offrir une présence digitale inégalée.',
    features: [
      'Sites web vitrine & corporate responsive',
      'Applications e-commerce performantes (WooCommerce, Shopify, custom)',
      'Applications mobiles iOS & Android (React Native, Flutter)',
      'PWA, Progressive Web Apps',
      'Intégration CMS (WordPress, Strapi, Sanity)',
      'Optimisation des performances et Core Web Vitals',
      'API REST & GraphQL',
      'Maintenance & support technique',
    ],
    seoTitle: 'Agence de développement Web et Mobile à Paris : Nexia Digital',
    seoKeywords: ['développement web', 'création site web', 'application mobile', 'agence web Paris'],
    metaDescription: 'Besoin de créer un site web ou développer une application mobile ? Nexia Digital accompagne Indépendants, PME et Startups à Paris et en Île-de-France.',
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&q=80',
  },
  {
    slug: 'design-graphique',
    title: 'Graphisme & Design',
    shortTitle: 'Graphisme',
    icon: 'fa-solid fa-palette',
    color: '#ed116c',
    tagline: "Donnez une identité visuelle forte qui marque les esprits",
    description: "Notre studio de design crée des identités visuelles percutantes, des supports de communication imprimés et digitaux, et des interfaces UI/UX qui convertissent. Chaque pixel est pensé pour refléter l'ADN de votre marque et séduire votre cible.",
    features: [
      'Création d\'identité visuelle & logo',
      'Charte graphique complète',
      'Design UI/UX pour web et mobile',
      'Supports print (flyers, brochures, affiches)',
      'Packaging et design produit',
      'Infographies et data visualization',
      'Templates réseaux sociaux',
      'Brand guidelines',
    ],
    seoTitle: 'Agence Graphisme Île-de-France : Identité Visuelle | Nexia',
    seoKeywords: ['graphisme', 'identité visuelle', 'design logo', 'agence graphisme Île-de-France'],
    metaDescription: 'Donnez du sens à votre image avec Nexia Digital. Expertise en branding, logos et design graphique pour affirmer votre identité de marque en France.',
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&q=80',
  },
  {
    slug: 'community-management',
    title: 'Community Management',
    shortTitle: 'Community',
    icon: 'fa-solid fa-mobile-screen',
    color: '#f5ed21',
    tagline: 'Fédérez une communauté engagée autour de votre marque',
    description: 'Notre équipe de community managers experts gère votre présence sur Facebook, Instagram, LinkedIn, TikTok et X. Nous créons du contenu stratégique, animons votre communauté et augmentons votre notoriété digitale avec des stratégies data-driven.',
    features: [
      'Stratégie de contenu social media',
      'Création et planification de posts',
      'Gestion de Facebook, Instagram, LinkedIn, TikTok, X',
      'Animation de communauté et modération',
      'Gestion des avis et réputation en ligne',
      'Reporting mensuel et analytics',
      'Veille concurrentielle',
      'Gestion de crise digitale',
    ],
    seoTitle: 'Agence de community management et gestion des réseaux sociaux',
    seoKeywords: ['community management', 'gestion réseaux sociaux', 'social media manager'],
    metaDescription: 'Explorez les services social media de Nexia Digital pour bâtir votre stratégie de communication sur les réseaux sociaux et renforcer votre présence sociale.',
    image: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=800&q=80',
  },
  {
    slug: 'audiovisuel-motion-design',
    title: 'Audiovisuel & Motion Design',
    shortTitle: 'Motion Design',
    icon: 'fa-solid fa-clapperboard',
    color: '#9999ff',
    tagline: 'Des contenus visuels qui captivent et convertissent',
    description: "Nexia Digital produit des vidéos corporate, spots publicitaires, motion graphics et animations 2D/3D qui donnent vie à votre marque. Du tournage à la post-production, nous gérons l'intégralité de votre production audiovisuelle.",
    features: [
      'Production vidéo corporate et institutionnelle',
      'Spots publicitaires TV et digital',
      'Motion graphics et animations 2D/3D',
      'Tutoriels et vidéos explicatives (explainer videos)',
      'Reels et contenus courts pour réseaux sociaux',
      'Générique, intro & outro pour YouTube',
      'Sous-titrage et post-production',
      'Drone & prises de vue aériennes',
    ],
    seoTitle: 'Agence Motion Design & Production Vidéo | Nexia Digital',
    seoKeywords: ['motion design', 'production vidéo', 'animation 2D 3D', 'agence audiovisuelle Île-de-France'],
    metaDescription: 'Captivez votre audience avec Nexia Digital, agence de motion design en Île-de-France. Spécialiste en production vidéo et animation sur mesure.',
    image: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=800&q=80',
  },
  {
    slug: 'marketing-digital',
    title: 'Marketing Digital & Publicité',
    shortTitle: 'Marketing Digital',
    icon: 'fa-solid fa-chart-line',
    color: '#4ade80',
    tagline: 'Des stratégies marketing qui génèrent un ROI mesurable',
    description: 'Maximisez votre retour sur investissement avec nos stratégies de marketing digital intégrées : SEO, Google Ads, Meta Ads, email marketing et growth hacking. Nous pilotons vos campagnes avec une approche 100% data-driven pour maximiser votre ROI.',
    features: [
      'Référencement naturel SEO (on-page & off-page)',
      'Publicité Google Ads (Search, Display, Shopping)',
      'Campagnes Meta Ads (Facebook & Instagram)',
      'Email marketing et automation',
      'Stratégie de contenu et inbound marketing',
      'Analyse et reporting de performance',
      'Optimisation du taux de conversion (CRO)',
      'Growth hacking et acquisition clients',
    ],
    seoTitle: 'Agence Marketing Digital à Paris & IDF | Nexia Digital',
    seoKeywords: ['marketing digital', 'Google Ads', 'SEO', 'agence marketing digital Paris'],
    metaDescription: 'Élaborez votre stratégie digitale en Île-de-France avec Nexia Digital. Notre agence de marketing digital pour PME à Paris génère des leads ultra-qualifiés.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
  },
  {
    slug: 'strategie-digitale',
    title: 'Stratégie & Consulting',
    shortTitle: 'Consulting',
    icon: 'fa-solid fa-brain',
    color: '#3a419a',
    tagline: 'Un regard expert pour accélérer votre transformation digitale',
    description: 'Nos consultants stratégiques vous accompagnent dans la définition et la mise en œuvre de votre transformation digitale. Audit, roadmap, conseil en positionnement, nous vous guidons avec une vision globale et une expertise sectorielle reconnue.',
    features: [
      'Audit digital complet',
      'Stratégie de transformation digitale',
      'Conseil en positionnement et branding',
      'Analyse de la concurrence et benchmarking',
      'Plan de communication intégré',
      'Formation et coaching d\'équipe',
      'KPI et tableau de bord stratégique',
      'Accompagnement et suivi post-implémentation',
    ],
    seoTitle: 'Agence de conseil en marketing digital en France',
    seoKeywords: ['consulting digital', 'stratégie digitale', 'transformation digitale', 'conseil marketing digital France'],
    metaDescription: 'Boostez votre présence avec Nexia Digital, agence de conseil en marketing digital intervenant en France. Expertise, stratégie et solutions web.',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80',
  },
  {
    slug: 'solutions-ia-sur-mesure',
    title: 'Solutions IA Sur Mesure',
    shortTitle: 'Intelligence IA',
    icon: 'fa-solid fa-robot',
    color: '#f5d000',
    tagline: 'Des intelligences artificielles qui travaillent pour vous 24h/24',
    description: 'Nexia Digital conçoit et intègre des solutions d\'intelligence artificielle sur mesure pour automatiser vos processus, booster votre marketing et offrir des expériences client exceptionnelles. De l\'agent conversationnel aux analytics prédictifs, nous mettons l\'IA au service de votre croissance.',
    features: [
      'Agent conversationnelle IA : assistants virtuels intelligents',
      'Chatbots IA avancés : solutions de messagerie automatisée',
      'Automatisation IA : workflows intelligents et intégrations CRM',
      'Analytics prédictifs : analyses avancées avec Machine Learning',
      'IA Marketing : suite complète de marketing digital IA',
      'Génération automatique de contenus (emails, posts, fiches produits)',
      'Pré-qualification client via formulaires intelligents',
      'Intégration API IA (OpenAI, Claude, Gemini...)',
    ],
    seoKeywords: ['solutions IA', 'chatbot IA', 'intelligence artificielle', 'automatisation IA', 'agent conversationnel'],
    metaDescription: 'Solutions IA sur mesure : chatbots intelligents, agents conversationnels, automatisation IA et analytics prédictifs. Nexia Digital propulse votre entreprise avec l\'IA.',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80',
  },
  {
    slug: 'seo-sea',
    title: 'SEO & SEA',
    shortTitle: 'SEO & SEA',
    icon: 'fa-solid fa-magnifying-glass-chart',
    color: '#4ade80',
    tagline: 'Dominez Google en référencement naturel et payant',
    description: 'Nexia Digital conçoit et pilote des stratégies SEO et SEA performantes pour propulser votre visibilité sur Google. Du référencement naturel à long terme aux campagnes Google Ads à fort ROI, nous combinons les deux leviers pour maximiser votre acquisition de trafic qualifié et générer un flux constant de leads.',
    features: [
      'Audit SEO complet (technique, on-page, off-page)',
      'Optimisation du référencement naturel & contenu',
      'SEO local & Google Business Profile',
      'Netlinking & stratégie de backlinks',
      'Campagnes Google Ads (Search, Display, Shopping, YouTube)',
      'Campagnes Bing Ads & retargeting',
      'Suivi des KPI et reporting mensuel détaillé',
      'A/B testing & optimisation du taux de conversion',
    ],
    seoTitle: 'Agence SEO & SEA à Paris | Référencement Google | Nexia Digital',
    seoKeywords: ['agence SEO', 'agence SEA', 'référencement naturel', 'Google Ads', 'référencement payant', 'SEO Paris'],
    metaDescription: 'Agence SEO & SEA à Paris. Nexia Digital booste votre visibilité sur Google avec des stratégies de référencement naturel et des campagnes Google Ads performantes.',
    image: 'https://images.unsplash.com/photo-1571677246347-5040036b95cc?w=800&q=80',
  },
];
